import React, { useState, useEffect, useCallback } from 'react';
import { Modal } from './Modal';
import { User, Channel, ChannelMember, OnlineUser, DEFAULT_AVATAR, handleAvatarError } from '../types';
import { resolveAvatarUrl } from '../utils/serverUrl';
import { channelService } from '../services/channelService';
import { socketService } from '../services/socketService';
import { Loader2, Crown, Shield, User as UserIcon } from 'lucide-react';
import { PresenceDot } from './PresenceDot';
import { STATUS_LABEL } from '../utils/presence';

interface MemberListModalProps {
  isOpen: boolean;
  onClose: () => void;
  channel: Channel;
  currentUser: User;
  onlineUsers: OnlineUser[];
  onUserClick?: (user: User) => void;
}

export const MemberListModal: React.FC<MemberListModalProps> = ({
  isOpen,
  onClose,
  channel,
  currentUser,
  onlineUsers,
  onUserClick,
}) => {
  const [members, setMembers] = useState<ChannelMember[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchMembers = useCallback(async (showSpinner = true) => {
    if (showSpinner) setLoading(true);
    setError(null);
    try {
      const data = await channelService.getMembers(channel.id);
      setMembers(data);
    } catch (err) {
      console.error('Failed to fetch members:', err);
      setError('멤버 목록을 불러오는데 실패했습니다.');
    } finally {
      if (showSpinner) setLoading(false);
    }
  }, [channel.id]);

  useEffect(() => {
    if (!isOpen) return;
    fetchMembers();
  }, [isOpen, fetchMembers]);

  // 모달이 열려 있는 동안의 멤버 변동(초대·퇴장·역할변경)을 반영한다.
  // 예전에는 열 때 1회만 조회해서, 보는 중에 누가 들어오거나 나가도 그대로였다.
  useEffect(() => {
    if (!isOpen) return;
    const socket = socketService.getSocket();
    if (!socket) return;

    let timer: ReturnType<typeof setTimeout> | null = null;
    const refetch = (payload: any) => {
      if (payload?.channelId !== channel.id) return;
      // 여러 명을 한꺼번에 초대하면 이벤트가 몰아치므로 살짝 묶는다.
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => fetchMembers(false), 300);
    };

    socket.on('member:added', refetch);
    socket.on('member:removed', refetch);
    socket.on('member:role-changed', refetch);
    return () => {
      if (timer) clearTimeout(timer);
      socket.off('member:added', refetch);
      socket.off('member:removed', refetch);
      socket.off('member:role-changed', refetch);
    };
  }, [isOpen, channel.id, fetchMembers]);

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'owner':
        return <Crown size={14} className="text-yellow-500" />;
      case 'admin':
        return <Shield size={14} className="text-blue-500" />;
      default:
        return null;
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'owner':
        return '방장';
      case 'admin':
        return '관리자';
      default:
        return '멤버';
    }
  };

  // 역할 순으로 정렬 (owner > admin > member)
  const sortedMembers = [...members].sort((a, b) => {
    const roleOrder = { owner: 0, admin: 1, member: 2 };
    return (roleOrder[a.role] || 2) - (roleOrder[b.role] || 2);
  });

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`멤버 목록 (${members.length}명)`}
      size="sm"
    >
      <div className="space-y-2">
        {loading ? (
          <div className="flex items-center justify-center py-8 text-slate-400">
            <Loader2 size={24} className="animate-spin mr-2" />
            불러오는 중...
          </div>
        ) : error ? (
          <div className="p-4 text-center text-brand-600">{error}</div>
        ) : members.length === 0 ? (
          <div className="p-4 text-center text-slate-400">멤버가 없습니다</div>
        ) : (
          <div className="max-h-80 overflow-y-auto -mx-2">
            {sortedMembers.map((member) => {
              const isMe = member.userId === String(currentUser.id);
              // 행마다 같은 find를 네 번 돌리던 것을 한 번으로. 사번 비교는 String() 정규화 규약
              const status =
                onlineUsers.find((u) => String(u.userId) === String(member.userId))?.status ?? 'offline';
              return (
                <div
                  key={member.userId}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg mx-2 ${isMe
                    ? 'bg-brand-100'
                    : 'hover:bg-slate-50 cursor-pointer'
                    } transition-colors`}
                  onClick={() => {
                    if (!isMe && onUserClick) {
                      onUserClick({
                        id: member.userId,
                        username: member.userName,
                        avatar: member.avatar || DEFAULT_AVATAR,
                        status: member.status || 'offline',
                      });
                      onClose();
                    }
                  }}
                >
                  {/* 아바타 */}
                  <div className="relative">
                    <img
                      src={resolveAvatarUrl(isMe ? currentUser.avatar : member.avatar)} onError={handleAvatarError}
                      alt=""
                      className="w-10 h-10 rounded-full bg-slate-100"
                    />
                    <PresenceDot
                      status={status}
                      className="absolute -bottom-0.5 -right-0.5 w-3 h-3 border-2 border-white"
                    />
                  </div>

                  {/* 사용자 정보 */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-slate-800 truncate">
                        {member.userName}
                        {isMe && <span className="text-slate-400 ml-1">(나)</span>}
                      </span>
                      {getRoleIcon(member.role)}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                      <span>{getRoleLabel(member.role)}</span>
                      {member.joinedAt && (
                        <>
                          <span>•</span>
                          <span>
                            {new Date(member.joinedAt).toLocaleDateString('ko-KR')} 참여
                          </span>
                        </>
                      )}
                    </div>
                  </div>

                  {/* 온라인 상태 텍스트 — 라벨은 presence.ts가 소유한다(예전엔 '자리비움'으로 어긋났다) */}
                  <div className="text-xs text-slate-500">{STATUS_LABEL[status]}</div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Modal>
  );
};
